/**
 * SSE Upload Utility
 *
 * Sends a FormData upload via fetch and reads the response as a
 * Server-Sent Events stream. Reports progress events in real-time
 * and resolves with the final result when the stream ends.
 *
 * Gracefully degrades: if the server responds with JSON (e.g. a
 * validation error before SSE starts), the JSON body is parsed and
 * returned as a normal result.
 */

export interface SSEProgressEvent {
  type: 'progress';
  phase: string;
  processed: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
}

export interface SSECompleteEvent {
  type: 'complete';
  success: boolean;
  message: string;
  created: number;
  updated: number;
  skipped: number;
}

export interface SSEErrorEvent {
  type: 'error';
  message: string;
}

export type SSEEvent = SSEProgressEvent | SSECompleteEvent | SSEErrorEvent;

/** The subset of progress data passed to UI callbacks. */
export interface ImportProgressData {
  phase: string;
  processed: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
}

export interface SSEUploadResult {
  success: boolean;
  message: string;
  created?: number;
  updated?: number;
  skipped?: number;
}

/**
 * Upload a file via fetch and consume the SSE progress stream.
 *
 * @param url       Full URL to POST to
 * @param formData  FormData containing the file
 * @param onProgress  Called for every `progress` SSE event
 * @param headers   Optional extra headers (e.g. auth)
 */
export async function sseUpload(
  url: string,
  formData: FormData,
  onProgress?: (data: ImportProgressData) => void,
  headers?: Record<string, string>,
): Promise<SSEUploadResult> {
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers,
  });

  // If server returned JSON (e.g. validation error before SSE started), handle it
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const result = await response.json();
    return {
      success: result.status === 200,
      message: result.message || 'Import failed',
    };
  }

  // Read the SSE stream
  if (!response.body) {
    return { success: false, message: 'Server did not return a stream' };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: SSEUploadResult | null = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();

    if (value) {
      buffer += decoder.decode(value, { stream: true });
    }

    // Split on double-newline (SSE event separator)
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || ''; // last element is the incomplete chunk

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed.startsWith('data: ')) continue;

      try {
        const event: SSEEvent = JSON.parse(trimmed.slice(6));

        if (event.type === 'progress' && onProgress) {
          onProgress({
            phase: event.phase,
            processed: event.processed,
            total: event.total,
            created: event.created,
            updated: event.updated,
            skipped: event.skipped,
          });
        } else if (event.type === 'complete') {
          finalResult = {
            success: event.success,
            message: event.message,
            created: event.created,
            updated: event.updated,
            skipped: event.skipped,
          };
        } else if (event.type === 'error') {
          finalResult = {
            success: false,
            message: event.message,
          };
        }
      } catch {
        // skip unparseable SSE lines
      }
    }

    if (done) break;
  }

  return finalResult || { success: false, message: 'Connection closed without a result' };
}
